// Thin wrapper around the Telegram WebApp global. We use the official
// `telegram-web-app.js` script (loaded in index.html) directly to avoid extra
// SDK churn. `@telegram-apps/sdk-react` is listed as a dep for future use.

export function getWebApp(): TelegramWebApp | null {
  if (typeof window === 'undefined') return null;
  return window.Telegram?.WebApp ?? null;
}

export function readInitData(): string {
  const wa = getWebApp();
  const fromTg = wa?.initData ?? '';
  if (fromTg) return fromTg;
  return import.meta.env.VITE_DEV_INIT_DATA ?? '';
}

export function readTgUser(): TelegramWebAppUser | null {
  const wa = getWebApp();
  return wa?.initDataUnsafe?.user ?? null;
}

/** The lounge brand is deliberately dark-only. We deliberately DO NOT
 *  inherit Telegram's light theme tokens — the CSS custom properties
 *  defined in index.css under :root are the source of truth. */
export function applyThemeFromTg(): void {
  const root = document.documentElement;
  root.classList.add('dark');
  root.classList.remove('light');
}

export function haptic(type: 'success' | 'error' | 'warning'): void {
  try {
    getWebApp()?.HapticFeedback?.notificationOccurred(type);
  } catch {
    /* noop in browser */
  }
}

/** Native Telegram modal popup (falls back to alert on older clients). Use for
 *  blocking notices the user must acknowledge, e.g. the active-bookings limit. */
export function showPopup(message: string, title?: string): void {
  const wa = getWebApp();
  try {
    if (wa?.showPopup) {
      wa.showPopup({ title, message, buttons: [{ type: 'ok' }] });
    } else if (wa?.showAlert) {
      wa.showAlert(message);
    } else if (typeof window !== 'undefined') {
      window.alert(message);
    }
  } catch {
    try {
      window.alert(message);
    } catch {
      /* noop */
    }
  }
}

export function openExternal(url: string): void {
  const wa = getWebApp();
  if (wa) wa.openLink(url);
  else window.open(url, '_blank', 'noopener');
}

export function bindBackButton(handler: (() => void) | null): void {
  const wa = getWebApp();
  const bb = wa?.BackButton;
  if (!bb) return;
  if (handler) {
    bb.onClick(handler);
    bb.show();
  } else {
    bb.hide();
  }
}

export function initTg(): void {
  const wa = getWebApp();
  if (!wa) return;
  try {
    wa.ready();
    wa.expand();
  } catch {
    /* ignore */
  }
  // Force Telegram's own chrome (header, background) to match our matte
  // black canvas so nothing brightens when the user has a light OS theme.
  try {
    (wa as unknown as {
      setHeaderColor?: (c: string) => void;
      setBackgroundColor?: (c: string) => void;
    }).setHeaderColor?.('#0a0a0b');
    (wa as unknown as {
      setBackgroundColor?: (c: string) => void;
    }).setBackgroundColor?.('#0a0a0b');
  } catch {
    /* older clients ignore */
  }
  applyThemeFromTg();
  wa.onEvent('themeChanged', applyThemeFromTg);
}
